import React, {createContext, useContext, useRef, useEffect, useState} from 'react'

import loadable from '@loadable/component'
import PropTypes from 'prop-types'
import {useQuery} from '@tanstack/react-query'

// rendered only after loadApi() is called, so keep the library out of the shared bundle
const LoadScript = loadable(() => import('@react-google-maps/api'), {
    resolveComponent: (module) => module.LoadScript,
    ssr: false
})

export const GoogleMapsScriptLoader = ({config, onLoad, onError, children}) => {
    const [internalKey, setInternalKey] = useState(JSON.stringify(config))

    // if Config changes -> remounte completly
    useEffect(() => {
        const newKey = JSON.stringify(config)
        setInternalKey(newKey)
    }, [config])

    return (
        <LoadScript
            loadingElement={<></>}
            key={internalKey}
            googleMapsApiKey={config.apiKey}
            language={config.locale}
            region={config.region}
            libraries={config.libraries}
            onLoad={onLoad}
            onError={onError}
        >
            {children}
        </LoadScript>
    )
}

GoogleMapsScriptLoader.propTypes = {
    config: PropTypes.shape({
        apiKey: PropTypes.string,
        locale: PropTypes.string,
        region: PropTypes.string,
        libraries: PropTypes.array
    }),
    onLoad: PropTypes.func,
    onError: PropTypes.func,
    children: PropTypes.node
}

const GoogleMapsAPIContext = createContext(null)

export const GoogleMapsAPIProvider = ({config, children}) => {
    const [isLoaded, setIsLoaded] = useState(false)
    const [loadError, setLoadError] = useState(null)
    const [loadRequested, setLoadRequested] = useState(false)

    const promiseRef = useRef(null)
    const resolverRef = useRef(null)

    if (!promiseRef.current) {
        promiseRef.current = new Promise((resolve, reject) => {
            resolverRef.current = {resolve, reject}
        })
    }

    useEffect(() => {
        setIsLoaded(false)
        setLoadError(null)
        promiseRef.current = new Promise((resolve, reject) => {
            resolverRef.current = {resolve, reject}
        })
    }, [config])

    const loadApi = () => {
        if (loadError) return Promise.reject(loadError)
        if (isLoaded && window.google && window.google.maps) {
            return Promise.resolve(window.google.maps)
        }

        setLoadRequested(true) // ← hier wird das Laden getriggert
        return promiseRef.current
    }

    return (
        <GoogleMapsAPIContext.Provider value={{loadApi}}>
            {loadRequested && (
                <GoogleMapsScriptLoader
                    config={config}
                    onLoad={() => {
                        setIsLoaded(true)
                        resolverRef.current?.resolve(window.google.maps)
                    }}
                    onError={(err) => {
                        setLoadError(err)
                        resolverRef.current?.reject(err)
                    }}
                ></GoogleMapsScriptLoader>
            )}
            {children}
        </GoogleMapsAPIContext.Provider>
    )
}

GoogleMapsAPIProvider.propTypes = {
    config: PropTypes.object,
    onLoad: PropTypes.func,
    onError: PropTypes.func,
    children: PropTypes.node
}

export const useGoogleMapsApiLoader = () => {
    const contextValue = useContext(GoogleMapsAPIContext)
    if (!contextValue) {
        throw new Error('useGoogleMapsApiLoader must be used within a GoogleMapsAPIProvider')
    }
    return contextValue.loadApi
}

export const useGeocodeAddress = (address, countryCode) => {
    const loadApi = useGoogleMapsApiLoader()

    const query = useQuery({
        queryKey: ['geocode', address, countryCode],
        enabled: !!address,
        queryFn: async () => {
            const maps = await loadApi()
            const geocoder = new maps.Geocoder()

            return new Promise((resolve, reject) => {
                geocoder.geocode(
                    {address, componentRestrictions: {country: countryCode}},
                    (results, status) => {
                        if (status === 'OK' && results[0]) {
                            const {lat, lng} = results[0].geometry.location
                            resolve({latitude: lat(), longitude: lng()})
                        } else {
                            reject(new Error(status))
                        }
                    }
                )
            })
        },
        staleTime: 1000 * 60 * 5,
        retry: false,
        meta: {displayName: 'useGeocodeAddress'}
    })

    return {
        coords: query.data ?? null,
        error: query.error?.message ?? null,
        loading: query.isLoading
    }
}
